/**
 * Turning what the editor holds into what goes on the wire (FEAT-028 wave 2).
 *
 * The editor thinks in its own shapes — a list of text bars, a list of strokes
 * with ids and timestamps it needs for undo and dragging. The server stores one
 * small versioned blob and nothing else. This is the seam, and it is also where
 * the CAPS live: the same limits `backend/app/utils/overlays.py` enforces, applied
 * here first so a long drawing is thinned to fit instead of being refused at
 * the door. A 400 from that endpoint should mean a bug on our side, never a
 * moment someone loses because they drew for too long.
 */
import type { DrawingStroke, TextBar } from '../types';
import { hasOverlays, type MomentOverlays } from './overlayRender';

/** Mirrors app/utils/overlays.py. Changing one without the other is the bug. */
export const OVERLAY_LIMITS = {
  maxJsonBytes: 64 * 1024,
  maxBars: 10,
  maxBarText: 200,
  maxStrokes: 200,
  maxPointsPerStroke: 2000,
  maxStrokeWidth: 0.5,
  /** Points closer together than this add nothing a finger can see. */
  minPointGap: 0.0025,
} as const;

/**
 * The editor's state → the blob, or null when there is nothing to draw.
 *
 * Null is the answer that matters most: it is what tells the server (and then
 * every viewer) that this moment carries no decoration, which is the same thing
 * every moment sent before this wave says.
 */
export function buildOverlays(
  textBars: TextBar[] | null | undefined,
  strokes: DrawingStroke[] | null | undefined
): MomentOverlays | null {
  const bars = (textBars ?? [])
    .map((bar) => ({
      text: (bar.text ?? '').trim().slice(0, OVERLAY_LIMITS.maxBarText),
      y: clamp(bar.y ?? 0, 0, 100),
    }))
    .filter((bar) => bar.text.length > 0)
    .slice(0, OVERLAY_LIMITS.maxBars);

  const drawn = (strokes ?? [])
    .slice(0, OVERLAY_LIMITS.maxStrokes)
    .map((stroke) => ({
      pts: simplify(stroke.points ?? []),
      color: stroke.color,
      w: clamp(stroke.w ?? 0, 0, OVERLAY_LIMITS.maxStrokeWidth),
    }))
    .filter((stroke) => stroke.pts.length >= 2);

  const overlays: MomentOverlays = { v: 1, bars, strokes: fitToBudget(drawn) };
  return hasOverlays(overlays) ? overlays : null;
}

/**
 * Thin strokes until the whole blob fits the byte cap.
 *
 * The per-stroke caps above bound one line; nothing bounded the SUM, and a
 * drawing that covers the picture can exceed 64 KB while every individual
 * stroke is legal. Serializing it would then produce a field the server
 * refuses — or worse, one this file drops on the floor, sending the moment
 * with no decoration at all. Halving points converges quickly (200 strokes at
 * two points each is a few KB), and losing sample density is invisible where
 * losing the drawing would not be.
 */
function fitToBudget(strokes: MomentOverlays['strokes']): MomentOverlays['strokes'] {
  let current = strokes;
  for (let round = 0; round < 12; round++) {
    if (JSON.stringify(current).length <= OVERLAY_LIMITS.maxJsonBytes - 2048) break;
    current = current.map((stroke) =>
      stroke.pts.length <= 2
        ? stroke
        : { ...stroke, pts: thin(stroke.pts, Math.max(2, Math.floor(stroke.pts.length / 2))) }
    );
  }
  return current;
}

/** The blob as the form field, or undefined when there is nothing to send. */
export function serializeOverlays(
  overlays: MomentOverlays | null | undefined
): string | undefined {
  if (!overlays || !hasOverlays(overlays)) return undefined;
  const json = JSON.stringify(overlays);
  if (json.length > OVERLAY_LIMITS.maxJsonBytes) {
    // Every cap above already applies, so reaching this is a bug in one of
    // them. Sending the moment WITHOUT its decoration beats not sending it.
    console.error('[overlays] payload over the cap, sending clean:', json.length);
    return undefined;
  }
  return json;
}

/** Convenience for the send paths: editor state straight to a form field. */
export function overlaysField(
  textBars: TextBar[] | null | undefined,
  strokes: DrawingStroke[] | null | undefined
): string | undefined {
  return serializeOverlays(buildOverlays(textBars, strokes));
}

/**
 * Drop points a finger could not have meant differently.
 *
 * A stroke arrives as one sample per pointer event — hundreds per second on a
 * fast phone, most of them a hair apart. Keeping them all makes the blob big
 * for no visible gain; dropping the ones inside `minPointGap` (a quarter of a
 * percent of the picture) is invisible and typically sheds most of them. The
 * last point is always kept so a stroke still ends where the finger lifted.
 */
function simplify(points: { x: number; y: number }[]): [number, number][] {
  if (points.length < 2) return [];
  const kept: [number, number][] = [[clamp01(points[0].x), clamp01(points[0].y)]];
  for (let i = 1; i < points.length - 1; i++) {
    const [lastX, lastY] = kept[kept.length - 1];
    const x = clamp01(points[i].x);
    const y = clamp01(points[i].y);
    if (Math.hypot(x - lastX, y - lastY) >= OVERLAY_LIMITS.minPointGap) {
      kept.push([x, y]);
    }
  }
  const end = points[points.length - 1];
  kept.push([clamp01(end.x), clamp01(end.y)]);
  return kept.length > OVERLAY_LIMITS.maxPointsPerStroke
    ? thin(kept, OVERLAY_LIMITS.maxPointsPerStroke)
    : kept;
}

/** Still too many after simplifying: keep every Nth, plus both ends. */
function thin(points: [number, number][], limit: number): [number, number][] {
  const step = points.length / (limit - 1);
  const out: [number, number][] = [];
  for (let i = 0; out.length < limit - 1; i += step) {
    out.push(points[Math.floor(i)]);
  }
  out.push(points[points.length - 1]);
  return out;
}

function clamp(value: number, low: number, high: number): number {
  if (!Number.isFinite(value)) return low;
  return value < low ? low : value > high ? high : value;
}

function clamp01(value: number): number {
  // Rounded to 0.01% of the picture: below what a finger can mean and below
  // what a pixel can show, and it nearly halves what a long stroke costs on
  // the wire.
  return Math.round(clamp(value, 0, 1) * 10000) / 10000;
}
